import { useState } from "react";
import dynamic from 'next/dynamic';
// @mui
import {
  Box,
  Pagination,
  Avatar,
  Card,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
// hooks
import useResponsive from "../../hooks/useResponsive";
// components
import Label from '../../components/Label';
import Iconify from "../../components/Iconify";
import SvgIconStyle from "../../components/SvgIconStyle";
// lottie
const DotLottiePlayer = dynamic(
  () => import('@dotlottie/react-player').then(mod => mod.DotLottiePlayer),
  { ssr: false }
);
import '@dotlottie/react-player/dist/index.css';

export default function ScoutPoolList({
  summaryData,
  scoutData,
  poolData,
  currentTab,
  setCurrentTab,
  selectCrew=null,
  // pagination
  pageCount,
  page,
  setPage,
}) {
  const smUp = useResponsive('up', 'sm');

  const TABS = [
    {
      label: '인재풀 전체',
      value: 'all',
      color: 'primary',
      count: summaryData?.all || 0,
    },
    {
      label: '커피챗 제안 가능',
      value: 'suggestable',
      color: 'success',
      count: summaryData?.suggestable || 0,
    },
    {
      label: '제안 완료',
      value: 'sent',
      color: 'default',
      count: summaryData?.sent || 0,
    },
    {
      label: '일정 확정 필요',
      value: 'confirm',
      color: 'info',
      count: summaryData?.confirm || 0,
    },
    {
      label: '미팅 예정/완료',
      value: 'reserved',
      color: 'default',
      count: summaryData?.reserved || 0,
    },
    {
      value: 'archived',
      label: '인재풀 제외',
      color: 'default',
      count: 0,
    }
  ];
  const isArchived = currentTab === "archived";
  const noScoutData = scoutData?.length === 0;
  const noCoffeeChatData = poolData?.length === 0;
  const hasPages = pageCount > 1;

  return (
    <Stack>
      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        value={currentTab}
        onChange={(e, newValue) => setCurrentTab(newValue)}
        TabIndicatorProps={{
          style: { ...currentTab === "archived" && { display: 'none' } }
        }}
        sx={{ mb: 2 }}
      >
        {TABS.map((tab, index) => (
          tab.value === "archived" ? (
            <IconButton key={index} value={"archived"} size="small" sx={{ color: "text.disabled" }} edge="end" onClick={() => {setCurrentTab("archived")}}>
              <SvgIconStyle src={"/icons/icon-archive-drop-mono.svg"} sx={{ width: 24, height: 24 }} />
            </IconButton>
          ) : (
            <Tab
              disabled={tab.count === 0}
              disableRipple
              key={index}
              value={tab.value}
              label={
                <Stack spacing={1} direction="row" alignItems="center">
                  <div>{tab.label}</div><Label variant={currentTab === tab.value ? "filled" : "ghost"} color={tab.color}>{tab.count}</Label>
                </Stack>
              }
            />
          )
        ))}
      </Tabs>

      <Stack spacing={1.5}>
        {poolData?.map((row, index) => (
          <Card key={index} sx={{ p: 2.5 }}
            onClick={() => selectCrew(row)}
          >
            <Stack direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Stack spacing={1} sx={{ width: "100%", display: "flex", overflow: "hidden" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar
                    src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crewji/crewji_${row?.crew?.randomhash || '5'}.png`}
                    sx={{ width: 32, height: 32 }}
                    alt={row?.crew?.nickname}
                    color={'default'}
                  />
                  <Typography variant="subtitle2" sx={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row?.crew?.nickname}
                  </Typography>
                  {isArchived ? (
                    <Label variant="outlined" color="default" sx={{ color: "text.secondary" }}>
                      인재풀 제외
                    </Label>
                  ) : (
                    <Label
                      color={
                          row?.coffee_chat_status === "wait" ? "success"
                        : row?.coffee_chat_status === "offer-to-company" ? "info"
                        : "default"
                      }
                    >
                      {row?.label}
                    </Label>
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">{row?.crew?.main_position}</Typography>
              </Stack>
              <Iconify icon="ic:chevron-right" sx={{ minWidth: 20, minHeight: 20, width: 20, height: 20, color: "text.disabled" }} />
            </Stack>
          </Card>
        ))}
        {hasPages && (
        <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            size={"medium"}
            count={pageCount}
            page={page}
            onChange={(e, v) => setPage(v)}
          />
        </Box>
        )}

        {isArchived && noCoffeeChatData && (
          <Box sx={{ px: 3, py: 7, mx: "auto" }} maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: 1,
                alignItems: "center", justifyContent: "center", textAlign: "center",
              }}
            >
              <Iconify icon="eva:alert-triangle-fill" width={24} height={24} sx={{ color: "text.disabled", mb: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                인재풀에서 제외한 크루가 없어요.
              </Typography>
              <Typography variant="caption">
                추천 받은 크루를 확인하고, 마음에 드는 크루를 인재풀에 추가해주세요.
              </Typography>
            </Box>
          </Box>
        )}

        {/* no scout & coffeechat data */}
        {!isArchived && !noScoutData && noCoffeeChatData && (
          <Box sx={{ px: 3, py: 7, mx: "auto" }} maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: 1,
                alignItems: "center", justifyContent: "center", textAlign: "center",
              }}
            >
              <Iconify icon="eva:alert-triangle-fill" width={24} height={24} sx={{ color: "text.disabled", mb: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                인재풀에 크루가 없어요.
              </Typography>
              <Typography variant="caption">
                추천 받은 크루를 확인하고, 마음에 드는 크루를 인재풀에 추가해주세요.
              </Typography>
            </Box>
          </Box>
        )}

        {/* no coffeechat */}
        {!isArchived && noScoutData && noCoffeeChatData && (
          <Box sx={{ px: 3, py: 7, mx: "auto" }} maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: 1,
                alignItems: "center", justifyContent: "center", textAlign: "center",
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                요청하신 인재를 찾고 있어요.
              </Typography>
              <Typography variant="caption">
                {smUp && '적합한 '}인재를 찾으면 카카오톡으로 알려드릴게요.
              </Typography>
              <DotLottiePlayer
                autoplay
                loop
                mode="normal"
                src={'/lottie/scout-finding.lottie'}
                style={{ width: smUp ? 350 : 250, height: smUp ? 200 : 150, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
              />
            </Box>
          </Box>
        )}
      </Stack>
    </Stack>
  )
}